import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { FEED_PERIOD_OPTIONS, FeedPeriod } from '../../models/feed-registration';

export interface FeedPeriodSelectProps {
  value: FeedPeriod;
  onChange: (period: FeedPeriod) => void;
  disabled?: boolean;
}

/**
 * Period picker for the feed views: all time, a preset look-back in days, or an explicit range.
 *
 * Days rather than the weeks the piglet widget counts in - feed is registered daily, so the useful
 * look-back is "the last week", not "the last seven weeks".
 *
 * Hand-rolled to match `PigletPeriodSelect`: this project loads Tailwind's theme and utilities
 * without preflight, so every native button style has to be reset explicitly.
 */
export default function FeedPeriodSelect({ value, onChange, disabled }: FeedPeriodSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const label = (): string => {
    if (value.kind === 'all') return t('f-feed-reg-period-all');
    if (value.kind === 'days') return t('f-feed-reg-period-days', { days: value.days });

    // Half-filled ranges are legitimate - the caller filters on whichever side is set.
    if (value.startDate && value.endDate) {
      return t('f-feed-reg-period-range', { from: value.startDate, to: value.endDate });
    }
    if (value.startDate) return t('f-feed-reg-period-from', { from: value.startDate });
    if (value.endDate) return t('f-feed-reg-period-until', { to: value.endDate });

    return t('f-feed-reg-period-custom');
  };

  const option = (key: string, text: string, selected: boolean, onPick: () => void) => (
    <button
      key={key}
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onPick}
      className={`flex w-full cursor-pointer appearance-none items-center gap-2 rounded-sm border-0 bg-transparent py-1.5 pr-2 pl-2 text-left text-sm transition-colors hover:bg-gray-100 ${
        selected ? 'text-gray-900' : 'text-gray-700'
      }`}
    >
      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
        {selected && <Check className="h-4 w-4 text-indigo-600" aria-hidden="true" />}
      </span>
      <span className="truncate">{text}</span>
    </button>
  );

  const setCustomBound = (bound: 'startDate' | 'endDate', day: string) => {
    const current = value.kind === 'custom' ? value : { kind: 'custom' as const, startDate: '', endDate: '' };

    onChange({ ...current, [bound]: day });
  };

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('f-feed-reg-period')}
        onClick={() => setOpen((isOpen) => !isOpen)}
        className="flex h-9 w-56 cursor-pointer appearance-none items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-900 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-default disabled:opacity-50"
      >
        <span className="truncate">{label()}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 z-50 mt-1 w-56 overflow-hidden rounded-md border border-gray-200 bg-white p-1 shadow-md"
        >
          {option('all', t('f-feed-reg-period-all'), value.kind === 'all', () => {
            onChange({ kind: 'all' });
            setOpen(false);
          })}

          {FEED_PERIOD_OPTIONS.map((days) =>
            option(
              String(days),
              t('f-feed-reg-period-days', { days }),
              value.kind === 'days' && value.days === days,
              () => {
                onChange({ kind: 'days', days });
                setOpen(false);
              }
            )
          )}

          {option('custom', t('f-feed-reg-period-custom'), value.kind === 'custom', () =>
            // Stays open: picking "custom" is choosing to type a range, not choosing a range.
            onChange(value.kind === 'custom' ? value : { kind: 'custom', startDate: '', endDate: '' })
          )}

          {value.kind === 'custom' && (
            <div className="mt-1 border-t border-gray-100 px-2 pt-2 pb-1">
              <label className="mb-1 block text-xs text-gray-500" htmlFor="feedPeriodFrom">
                {t('f-com-from')}
              </label>
              <input
                id="feedPeriodFrom"
                type="date"
                value={value.startDate}
                max={value.endDate || undefined}
                onChange={(event) => setCustomBound('startDate', event.target.value)}
                className="mb-2 w-full appearance-none rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900"
              />

              <label className="mb-1 block text-xs text-gray-500" htmlFor="feedPeriodTo">
                {t('f-com-to')}
              </label>
              <input
                id="feedPeriodTo"
                type="date"
                value={value.endDate}
                min={value.startDate || undefined}
                onChange={(event) => setCustomBound('endDate', event.target.value)}
                className="w-full appearance-none rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
